import { openDB, type DBSchema, type IDBPDatabase } from 'idb'
import {
  SCHEMA_VERSION,
  type AppPreferences,
  type Favourite,
  type KataPerformance,
  type TechniqueNote,
  type VideoReference,
} from './schema'

interface JudoKataDB extends DBSchema {
  references: {
    key: string
    value: VideoReference
    indexes: { 'by-kata': string }
  }
  notes: {
    key: string
    value: TechniqueNote
    indexes: { 'by-kata': string }
  }
  favourites: {
    key: string
    value: Favourite
    indexes: { 'by-kata': string }
  }
  performances: {
    key: string
    value: KataPerformance
    indexes: { 'by-kata': string }
  }
  preferences: {
    key: string
    value: AppPreferences & { id: string }
  }
  meta: {
    key: string
    value: { key: string; schemaVersion: number }
  }
}

const DB_NAME = 'fs-judo-kata'
const PREFS_KEY = 'app'

let dbPromise: Promise<IDBPDatabase<JudoKataDB>> | null = null

export function getDb(): Promise<IDBPDatabase<JudoKataDB>> {
  if (!dbPromise) {
    dbPromise = openDB<JudoKataDB>(DB_NAME, SCHEMA_VERSION, {
      upgrade(db, oldVersion) {
        if (oldVersion < 1) {
          if (!db.objectStoreNames.contains('references')) {
            const store = db.createObjectStore('references', { keyPath: 'id' })
            store.createIndex('by-kata', 'kataId')
          }
          if (!db.objectStoreNames.contains('preferences')) {
            db.createObjectStore('preferences', { keyPath: 'id' })
          }
          if (!db.objectStoreNames.contains('meta')) {
            db.createObjectStore('meta', { keyPath: 'key' })
          }
        }
        if (oldVersion < 2) {
          if (!db.objectStoreNames.contains('notes')) {
            const notes = db.createObjectStore('notes', { keyPath: 'id' })
            notes.createIndex('by-kata', 'kataId')
          }
          if (!db.objectStoreNames.contains('favourites')) {
            const favs = db.createObjectStore('favourites', { keyPath: 'id' })
            favs.createIndex('by-kata', 'kataId')
          }
        }
        // v3: note body → BlockNote `content` (lazy normalize on read also covers this).
        if (oldVersion < 3) {
          // Object stores unchanged; schema bump reserves the version for exports.
        }
        if (oldVersion < 4) {
          if (!db.objectStoreNames.contains('performances')) {
            const performances = db.createObjectStore('performances', { keyPath: 'id' })
            performances.createIndex('by-kata', 'kataId')
          }
        }
      },
    }).then(async (db) => {
      await db.put('meta', { key: 'schema', schemaVersion: SCHEMA_VERSION })
      return db
    })
  }
  return dbPromise
}

/** Test helper — closes and forgets the shared connection. */
export async function resetDbConnection(): Promise<void> {
  if (dbPromise) {
    const db = await dbPromise
    db.close()
    dbPromise = null
  }
}

export { PREFS_KEY }
export type { AppPreferences, Favourite, KataPerformance, TechniqueNote, VideoReference }
